import React, { useState, useRef, useEffect } from "react";
import { useTranslate } from "../../context/TranslateContext";
import "./index.scss";

const LanguageSelectorWithPortal = () => {
  const { currentLang, languages, changeLanguage } = useTranslate();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  const currentLangData = languages.find((l) => l.code === currentLang) || languages[0];

  // Position dropdown when opened
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 150; // min-width of dropdown
      
      // Calculate left position so dropdown right edge aligns with button right edge
      let leftPos = rect.right - dropdownWidth;
      
      // Make sure it doesn't go off the left edge of the screen
      if (leftPos < 10) {
        leftPos = 10;
      }
      
      setDropdownPosition({
        top: rect.bottom + 5,
        left: leftPos,
      });
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        buttonRef.current && 
        !buttonRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  // Close on scroll
  useEffect(() => {
    const handleScroll = () => setIsOpen(false);
    if (isOpen) {
      window.addEventListener("scroll", handleScroll, true);
    }
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [isOpen]);

  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
    setIsOpen(false);
  };

  return (
    <>
      <div className="language-selector">
        <button
          ref={buttonRef}
          className="language-selector-btn"
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-label="Select language"
        >
          <span className="lang-flag">{currentLangData.flag}</span>
          <span className={`lang-arrow ${isOpen ? "open" : ""}`}>▼</span>
        </button>
      </div>

      {isOpen && (
        <ul
          ref={dropdownRef}
          className="language-dropdown"
          role="listbox"
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
          }}
        >
          {languages.map((lang) => (
            <li
              key={lang.code}
              role="option"
              aria-selected={lang.code === currentLang}
              className={`language-option ${lang.code === currentLang ? "active" : ""}`}
              onClick={() => handleLanguageChange(lang.code)}
            >
              <span className="lang-flag">{lang.flag}</span>
              <span className="lang-name">{lang.name}</span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

export default LanguageSelectorWithPortal;